/**
 * Official OpenAI model list offered per assistant.
 *
 * The OpenAI key belongs to the customer's own account (`account_integrations.token_openai`),
 * so the cost tier here is guidance for whoever picks the model — it is not billing, and no
 * consumption is measured or stored anywhere.
 *
 * Assistants created before this list existed keep running on whatever they were on (`gpt-4o`
 * in practice). Their model is locked: see `isLegacyModel`. Mirrored for the edge runtime in
 * `supabase/functions/_shared/ai-models.ts` — Deno cannot import from `src/`.
 */

export type AiModelId = 'gpt-5.6-luna' | 'gpt-5.6-terra' | 'gpt-5.6-sol'

/** Relative cost of a model. The visible text lives in i18n (`assistants:configure.costTier*`). */
export type AiModelCostTier = 'low' | 'medium' | 'high'

/** New assistants are born on the cheapest model rather than inheriting the previous generation. */
export const DEFAULT_AI_MODEL: AiModelId = 'gpt-5.6-luna'

export interface AiModelOption {
  value: AiModelId
  /** Product name — deliberately not translated. */
  label: string
  costTier: AiModelCostTier
}

/** Ordered cheapest to most expensive, so the default sits first in the dropdown. */
export const AI_MODEL_OPTIONS: ReadonlyArray<AiModelOption> = [
  { value: 'gpt-5.6-luna', label: 'GPT-5.6 Luna', costTier: 'low' },
  { value: 'gpt-5.6-terra', label: 'GPT-5.6 Terra', costTier: 'medium' },
  { value: 'gpt-5.6-sol', label: 'GPT-5.6 Sol', costTier: 'high' },
]

export function isAiModelId(value: unknown): value is AiModelId {
  return AI_MODEL_OPTIONS.some((option) => option.value === value)
}

/**
 * True when the assistant runs a model outside the official list — i.e. it predates it.
 *
 * Those assistants stay on their model: switching them would hand a reasoning model a
 * conversation chain built by a non-reasoning one, which the Responses API does not support.
 * To move to a new model the user creates a new assistant.
 */
export function isLegacyModel(value: unknown): boolean {
  return typeof value === 'string' && value.trim().length > 0 && !isAiModelId(value.trim())
}

export function getAiModelLabel(value: unknown): string {
  const match = AI_MODEL_OPTIONS.find((option) => option.value === value)
  return match ? match.label : String(value ?? '')
}
